import {
  Box,
  BoxProps,
  CloseButton,
  Drawer,
  DrawerContent,
  Flex,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { IconType } from 'react-icons';
import { CiMoneyBill } from 'react-icons/ci';
import { FiFile, FiLayers, FiMonitor, FiUsers } from 'react-icons/fi';
import { MdGroups } from 'react-icons/md';
import { SiClickup } from 'react-icons/si';
import { useLocation } from 'react-router-dom';

import { getPrefixedQueryParamKey } from '@/hooks/useQueryParams';

import MobileNav from './MobileNav';
import NavItem from './NavItem';
import { ROUTES } from '../../../../routes/Routes';
import { caseClusterTablePrefix } from '../AdminCaseClusterList/AdminCaseClusterListClient';
import { patientCaseTablePrefix } from '../AdminCaseList/AdminCaseListClient';
import { clientTablePrefix } from '../AdminClientList/AdminClientListClient';
import { masterClassListTablePrefix } from '../AdminMasterclassList/AdminMasterclassListClient';
import { meetingTablePrefix } from '../AdminMeetingList/AdminMeetingListClient';
import { teamTablePrefix } from '../AdminTeamList/AdminTeamListClient';
import { usersTablePrefix } from '../AdminUserList/AdminUserListClient';

interface LinkItemProps {
  name: string;
  icon: IconType;
  path: string;
}
const LinkItems: Array<LinkItemProps> = [
  {
    name: 'Clients',
    path: `${ROUTES.ADMIN_CLIENTS}?${getPrefixedQueryParamKey('order', clientTablePrefix)}=createdAt&${getPrefixedQueryParamKey('orderDir', clientTablePrefix)}=desc`,
    icon: MdGroups,
  },
  {
    name: 'Teams',
    path: `${ROUTES.ADMIN_TEAMS}?${getPrefixedQueryParamKey('order', teamTablePrefix)}=createdAt&${getPrefixedQueryParamKey('orderDir', teamTablePrefix)}=desc`,
    icon: MdGroups,
  },
  {
    name: 'Users',
    path: `${ROUTES.ADMIN_USER_LIST}?${getPrefixedQueryParamKey('order', usersTablePrefix)}=createdAt&${getPrefixedQueryParamKey('orderDir', usersTablePrefix)}=desc`,
    icon: FiUsers,
  },
  {
    name: 'Meetings',
    path: `${ROUTES.ADMIN_MEETING_LIST}?${getPrefixedQueryParamKey('order', meetingTablePrefix)}=eventDate&${getPrefixedQueryParamKey('orderDir', meetingTablePrefix)}=desc`,
    icon: FiLayers,
  },
  {
    name: 'MasterClasses',
    path: `${ROUTES.ADMIN_MASTERCLASS_LIST}?${getPrefixedQueryParamKey('order', masterClassListTablePrefix)}=createdAt&${getPrefixedQueryParamKey('orderDir', masterClassListTablePrefix)}=desc`,
    icon: FiLayers,
  },
  {
    name: 'Cases',
    path: `${ROUTES.ADMIN_CASE_LIST}?${getPrefixedQueryParamKey('order', patientCaseTablePrefix)}=creationDate&${getPrefixedQueryParamKey('orderDir', patientCaseTablePrefix)}=desc`,
    icon: FiFile,
  },
  {
    name: 'CaseClusters',
    path: `${ROUTES.ADMIN_CASE_CLUSTER_LIST}?${getPrefixedQueryParamKey('order', caseClusterTablePrefix)}=createdAt&${getPrefixedQueryParamKey('orderDir', caseClusterTablePrefix)}=desc`,
    icon: FiMonitor,
  },
  {
    name: 'Clickup',
    path: ROUTES.ADMIN_CLICKUP,
    icon: SiClickup,
  },
  {
    name: 'Sales',
    path: ROUTES.ADMIN_SALES,
    icon: CiMoneyBill,
  },
];

const leftNavWidth = 44;

export default function Sidebar({ children }: { children: ReactNode }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <Box h="100%">
      {/* // --------------------- DESKTOP ------------------------ */}
      <SidebarContent onClose={() => onClose} display={{ base: 'none', md: 'block' }} pos="fixed" />

      {/* // --------------------- MOBILE (nav + drawer) ------------------------*/}
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} pos="relative" />
        </DrawerContent>
      </Drawer>
      <MobileNav display={{ base: 'flex', md: 'none' }} onOpen={onOpen} />

      {/* // --------------------- RIGHT AREA ------------------------*/}
      <Box
        ml={{ base: 0, md: leftNavWidth }}
        p="4"
        overflow="auto"
        bg={useColorModeValue('gray.100', 'gray.900')}
        minH="100%" // force background to take full page
        h="100%"
      >
        {children}
      </Box>
    </Box>
  );
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const SidebarContent = ({ onClose, ...rest }: SidebarProps) => {
  const { pathname } = useLocation();
  const activeSection = pathname;

  return (
    <Box
      bg={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: leftNavWidth }}
      h="full"
      marginTop="1px"
      {...rest}
    >
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      {LinkItems.map((link) => (
        <NavItem
          key={link.name}
          isActive={link.path.split('?')[0] === activeSection}
          icon={link.icon}
          path={link.path}
          onClick={onClose}
        >
          {link.name}
        </NavItem>
      ))}
    </Box>
  );
};
